import React, { useEffect, useMemo, useRef } from "react";

import Button from "devextreme-react/button";
import Form, { FormRef } from "devextreme-react/form";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { call } from "effection";
import { useTranslation } from "react-i18next";
import Balancer from "react-wrap-balancer";

import { Case, CaseElse, Switch } from "@app/contexts";
import {
  contractorInEditMode,
  contractorInSpvMode,
  SIZES,
  SPV_CONTRACTOR_MIRROR,
} from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import {
  selectCurrentMessagePartnersCIF,
  selectMessageType,
} from "@app/statefx/selectors/uiSpvMain.sel";
import { selectCurrentMirroredTab } from "@app/statefx/selectors/uiSpvMainContractor.sel";
import { sfxNotify } from "@app/statefx/xThunks";
import { setContractorsEditingProp } from "@app/statefx/xThunks/uiContractorsEditing";
import { fxReacttSelectedMirrorecContractor } from "@app/statefx/xThunks/uiDrawer";
import {
  fxExecuteAnafQuery,
  incrementCff,
  incrementCffArchive,
  incrementCffTmp,
  saveContractorToDb,
} from "@app/statefx/xThunks/uiSpvMain";
import "@app/style/dx.generic.catetory-chooser-compact.min.css";
import {
  TContractor,
  TContractorFormSource,
  TSPVResultType,
} from "@app/types/insider";

import "./contractors.scss";
import { isAPossibleCIF, normalizeCIF } from "./helpers";

export const HeadContent = ({
  frmConRef,
  bttnOptions,
}: {
  frmConRef: React.RefObject<FormRef>;
  bttnOptions?: any;
}) => {
  const { t } = useTranslation();
  const dispatch = useFxDispatch();

  const isRunningRef = useRef(false);

  const saveButtonClick = async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    const data = frmConRef.current?.instance().option("formData");
    let passes = true;
    if (!data.UsingRoEfactura) {
      dispatch(
        sfxNotify({
          type: "warning",
          message: "Se continua fara validarile eFactura.",
          duration: 3000,
          position: "bottom",
        })
      );
      passes = true;
    } else {
      const validationResult = await frmConRef.current?.instance().validate();
      passes = validationResult?.isValid ?? false;
    }
    if (passes) {
      dispatch(saveContractorToDb(data));
    }
    isRunningRef.current = false;
  };

  return (
    <Toolbar>
      <Item
        location="after"
        widget="dxButton"
        options={{
          icon: "refresh",
          hint: t("Cancel_changes"),
          type: "default",
          stylingMode: "contained",
          onClick: () => {
            //trigger reload
            dispatch(incrementCff());
            dispatch(incrementCffTmp());
            dispatch(incrementCffArchive());
          },
          ...bttnOptions,
        }}
      />
      <Item
        location="after"
        widget="dxButton"
        options={{
          icon: "save",
          hint: t("Save_in_main"),
          type: "success",
          stylingMode: "contained",
          onClick: saveButtonClick,
          ...bttnOptions,
        }}
      />
    </Toolbar>
  );
};

export const HeaderComponent = ({
  source,
  frmConRef,
  copyAll,
  runRefresh,
  buttonState,
  outOfScopeOfTVA,
  bttnOptions,
  caller,
}: {
  source: TContractorFormSource;
  frmConRef: React.RefObject<FormRef>;
  copyAll: () => void;
  runRefresh: () => void;
  buttonState: TContractor;
  outOfScopeOfTVA?: boolean | undefined;
  bttnOptions?: any;
  caller: typeof contractorInEditMode | typeof contractorInSpvMode;
}) => {
  const dispatch = useFxDispatch();
  const selectedCtorMirrorTab = useFxSelector(selectCurrentMirroredTab);
  const targetCIF = useFxSelector(selectCurrentMessagePartnersCIF);
  const editingState = useFxSelector(schema.uiContractorsEditing.select);
  const areAnyButtonsActive = useMemo(() => {
    return Object.values(buttonState).some((v) => v);
  }, [buttonState]);

  const onItemClick = (e) => {
    dispatch(fxReacttSelectedMirrorecContractor(e.itemData.id));
  };

  const buttonGroupOperationsItemClick = (e) => {
    if (e.itemData.id === "copyall") {
      copyAll();
    } else if (e.itemData.id === "refresh") {
      runRefresh();
    }
  };

  useEffect(() => {
    if (source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF) {
      if (targetCIF) {
        dispatch(fxExecuteAnafQuery(targetCIF));
      }
    }
  }, [source, targetCIF]);

  return (
    <div>
      <Switch value={source}>
        <Case when={"CFF_MAIN"}>
          <div className="contractor-head-cff">
            {caller === contractorInSpvMode ? (
              <Balancer>{"Datele de facturare"}</Balancer>
            ) : (
              <div className="header-switch-buttons">
                <Balancer>
                  {"Introducerea sau editarea datelor de facturare"}
                </Balancer>
              </div>
            )}
            <HeadContent frmConRef={frmConRef} bttnOptions={bttnOptions} />
          </div>
        </Case>
        <CaseElse>
          {caller === contractorInSpvMode ? (
            <Balancer>{"Preluare din alte surse de date"}</Balancer>
          ) : (
            <div className="header-switch-buttons">
              <Button
                className={`header-switch-button ${
                  source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF ? "active" : ""
                }`}
                stylingMode="text"
                text="Preluare de la ANAF"
                onClick={() => {
                  dispatch(fxReacttSelectedMirrorecContractor("ANAF"));
                }}
              ></Button>
              <Button
                className={`header-switch-button ${
                  source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_TMP ? "active" : ""
                }`}
                stylingMode="text"
                text="Preluare din XML temporar"
                onClick={() => {
                  dispatch(fxReacttSelectedMirrorecContractor("CFF_TMP"));
                }}
              ></Button>
            </div>
          )}
          <div>
            <Toolbar
              id="contractor-mirror-tabs"
              className="dx-swatch-catetory-chooser-compact"
            >
              <Item
                location="before"
                widget="dxButtonGroup"
                options={{
                  items: [
                    {
                      id: "copyall",
                      elementAttr: { id: "copyall" },
                      icon: "chevrondoubleleft",
                      disabled: !areAnyButtonsActive,
                      hint: "Copierea tuturor campurilor diferite, in formularul cu date de facturare",
                    },
                    {
                      id: "refresh",
                      icon: "refresh",
                      hint: "Reverificarea corespondetei datelor din formularul curent cu datele de facturare",
                    },
                  ],
                  onItemClick: buttonGroupOperationsItemClick,
                }}
              />
              <Item
                location="before"
                cssClass="dx-toolbar-alert"
                text={outOfScopeOfTVA === false ? "NU TVA" : ""}
              />
              <Item
                location="center"
                widget="dxTextBox"
                visible={
                  caller === contractorInEditMode &&
                  source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF
                }
                options={{
                  placeholder:
                    source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF
                      ? "Catare CIF"
                      : "Cautare Nume/Cif",
                  value: editingState?.searchCIF || "",
                  onValueChanged: (e) => {
                    if (e.value !== editingState?.searchCIF) {
                      dispatch(
                        setContractorsEditingProp({ searchCIF: e.value })
                      );
                    }
                  },
                  showClearButton: true,
                  isValid:
                    !editingState?.searchCIF ||
                    isAPossibleCIF(editingState?.searchCIF),

                  buttons: [
                    {
                      name: "search",
                      location: "after",
                      options: {
                        icon: "search",
                        onClick: () => {
                          if (isAPossibleCIF(editingState.searchCIF)) {
                            const normCif = normalizeCIF(
                              editingState.searchCIF
                            );
                            dispatch(fxExecuteAnafQuery(`${normCif}--force`));
                          }
                        },
                      },
                    },
                  ],
                }}
              />
              <Item
                location="after"
                widget="dxTabs"
                locateInMenu="auto"
                visible={caller === contractorInSpvMode}
                options={{
                  selectionMode: "single",
                  keyExpr: "id",
                  items: [
                    {
                      id: SPV_CONTRACTOR_MIRROR.CTR_MIRR_TMP,
                      icon: "file",
                      hint: "Extrase din fisierul XML",
                      text: "XML",
                      // visible: theMessageType === "FACTURA PRIMITA"
                    },
                    {
                      id: SPV_CONTRACTOR_MIRROR.CTR_MIRR_ARCHIVE,
                      icon: "card",
                      hint: "Date existente in modulul de Contacte sau Arhivate",
                      text: "Local ",
                    },
                    {
                      id: SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF,
                      icon: "globe",
                      hint: "Interogarea site-ului ANAF",
                      text: "ANAF",
                    },
                  ],
                  selectedItemKeys: [selectedCtorMirrorTab],
                  onItemClick: onItemClick,
                }}
              />
            </Toolbar>
          </div>
        </CaseElse>
      </Switch>
    </div>
  );
};
